import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Bell, Plus, Send, Users, User, Globe, Calendar, Loader2, X, Mail, Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

const STATUS_STYLES = {
  sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  sending: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-600 border-gray-200',
};

const RECIPIENT_ICONS = { user: User, cohort: Users, all: Globe };

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [cohorts, setCohorts] = useState([]);

  const [form, setForm] = useState({
    recipientType: 'all',
    recipientIds: [],
    type: 'system',
    title: '',
    message: '',
    link: '',
    ctaLabel: '',
    channels: { inApp: true, email: false },
    broadcast: { enabled: false, dismissible: true, expiresAt: '' },
    deliverAt: '',
  });

  useEffect(() => {
    fetchNotifications();
    fetchUsersAndCohorts();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/notifications');
      setNotifications(res.data.notifications || []);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersAndCohorts = async () => {
    try {
      const [usersRes, cohortsRes] = await Promise.all([
        axios.get('/api/admin/participants?limit=200').catch(() => ({ data: { participants: [] } })),
        axios.get('/api/admin/cohorts').catch(() => ({ data: { cohorts: [] } })),
      ]);
      setUsers(usersRes.data.participants || []);
      setCohorts(cohortsRes.data.cohorts || []);
    } catch (e) { /* ignore */ }
  };

  const handleSend = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (form.recipientType === 'user' && form.recipientIds.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    try {
      setSending(true);
      const payload = {
        ...form,
        deliverAt: form.deliverAt || null,
        broadcast: form.recipientType === 'all' ? form.broadcast : { enabled: false },
      };
      const res = await axios.post('/api/admin/notifications/send', payload);
      toast.success(form.deliverAt ? 'Notification scheduled' : `Sent to ${res.data.sentCount} user(s)`);
      setShowCreate(false);
      setForm({
        recipientType: 'all', recipientIds: [], type: 'system', title: '', message: '',
        link: '', ctaLabel: '', channels: { inApp: true, email: false },
        broadcast: { enabled: false, dismissible: true, expiresAt: '' }, deliverAt: '',
      });
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await axios.put(`/api/admin/notifications/${id}/cancel`);
      toast.success('Notification cancelled');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  return (
    <div data-testid="admin-notifications-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl font-semibold flex items-center gap-2">
            <Bell className="w-6 h-6 text-primary" /> Notifications
          </h1>
          <p className="text-muted-foreground mt-1">Send in-app notifications, emails, and broadcast announcements</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-notification-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Notification
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="pt-4 pb-4">
          <p className="text-xs text-muted-foreground">Total Sent</p>
          <p className="text-2xl font-semibold">{notifications.filter(n => n.status === 'sent').length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4">
          <p className="text-xs text-blue-600">Scheduled</p>
          <p className="text-2xl font-semibold text-blue-700">{notifications.filter(n => n.status === 'scheduled').length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4">
          <p className="text-xs text-emerald-600">Broadcasts</p>
          <p className="text-2xl font-semibold text-emerald-700">{notifications.filter(n => n.broadcast?.enabled).length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4">
          <p className="text-xs text-red-600">Failed</p>
          <p className="text-2xl font-semibold text-red-700">{notifications.filter(n => n.status === 'failed').length}</p>
        </CardContent></Card>
      </div>

      {/* Notifications Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-semibold text-lg mb-2">No notifications sent yet</h3>
            <p className="text-muted-foreground mb-6">Create your first notification to engage with participants.</p>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" /> Create Notification
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Channels</TableHead>
                  <TableHead>Delivered</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifications.map(n => {
                  const RecipientIcon = RECIPIENT_ICONS[n.recipientType] || Globe;
                  return (
                    <TableRow key={n.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{n.title}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-xs">{n.message}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <RecipientIcon className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs capitalize">{n.recipientType === 'all' ? 'All Users' : n.recipientType}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {n.channels?.inApp && <Badge variant="outline" className="text-xs py-0">In-App</Badge>}
                          {n.channels?.email && <Badge variant="outline" className="text-xs py-0"><Mail className="w-3 h-3 mr-1" />Email</Badge>}
                          {n.broadcast?.enabled && <Badge variant="outline" className="text-xs py-0 bg-violet-50 text-violet-700">Banner</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{n.sentCount || 0}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${STATUS_STYLES[n.status] || ''}`}>
                          {n.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{n.createdAt ? formatDate(n.createdAt) : '-'}</TableCell>
                      <TableCell>
                        {n.status === 'scheduled' && (
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(n.id)}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create Notification Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Notification</DialogTitle>
            <DialogDescription>Send an in-app notification, email, or broadcast announcement.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Recipient */}
            <div className="space-y-2">
              <Label>Send To</Label>
              <Select value={form.recipientType} onValueChange={(v) => setForm({ ...form, recipientType: v, recipientIds: [] })}>
                <SelectTrigger data-testid="recipient-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all"><div className="flex items-center gap-2"><Globe className="w-4 h-4" /> All Users</div></SelectItem>
                  <SelectItem value="cohort"><div className="flex items-center gap-2"><Users className="w-4 h-4" /> Cohort</div></SelectItem>
                  <SelectItem value="user"><div className="flex items-center gap-2"><User className="w-4 h-4" /> Individual User</div></SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.recipientType === 'user' && (
              <div className="space-y-2">
                <Label>Select User</Label>
                <Select value={form.recipientIds[0] || ''} onValueChange={(v) => setForm({ ...form, recipientIds: [v] })}>
                  <SelectTrigger><SelectValue placeholder="Choose a user..." /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id || u._id} value={u.id || u._id}>
                        {u.firstName} {u.lastName} ({u.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.recipientType === 'cohort' && (
              <div className="space-y-2">
                <Label>Select Cohort</Label>
                <Select value={form.recipientIds[0] || ''} onValueChange={(v) => setForm({ ...form, recipientIds: [v] })}>
                  <SelectTrigger><SelectValue placeholder="Choose a cohort..." /></SelectTrigger>
                  <SelectContent>
                    {cohorts.map(c => (
                      <SelectItem key={c.id || c._id} value={c.id || c._id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Content */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Notification title" data-testid="notif-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                    <SelectItem value="new_resource">New Resource</SelectItem>
                    <SelectItem value="cohort_update">Cohort Update</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Notification message..." rows={3} data-testid="notif-message-input" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link (optional)</Label>
                <Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label>CTA Button Label</Label>
                <Input value={form.ctaLabel} onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })} placeholder="View Details" />
              </div>
            </div>

            {/* Channels */}
            <div className="space-y-3">
              <Label>Delivery Channels</Label>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={form.channels.inApp} onCheckedChange={(v) => setForm({ ...form, channels: { ...form.channels, inApp: v } })} />
                  <Label className="font-normal flex items-center gap-1"><Bell className="w-4 h-4" /> In-App</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.channels.email} onCheckedChange={(v) => setForm({ ...form, channels: { ...form.channels, email: v } })} />
                  <Label className="font-normal flex items-center gap-1"><Mail className="w-4 h-4" /> Email</Label>
                </div>
              </div>
            </div>

            {/* Broadcast Banner */}
            {form.recipientType === 'all' && (
              <div className="space-y-3 p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Dashboard Banner</Label>
                    <p className="text-xs text-muted-foreground">Show as a banner on participant dashboards</p>
                  </div>
                  <Switch checked={form.broadcast.enabled} onCheckedChange={(v) => setForm({ ...form, broadcast: { ...form.broadcast, enabled: v } })} />
                </div>
                {form.broadcast.enabled && (
                  <div className="space-y-2">
                    <Label className="text-xs">Expires At (optional)</Label>
                    <Input type="datetime-local" value={form.broadcast.expiresAt} onChange={(e) => setForm({ ...form, broadcast: { ...form.broadcast, expiresAt: e.target.value } })} />
                  </div>
                )}
              </div>
            )}

            {/* Schedule */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Clock className="w-4 h-4" /> Schedule (optional)</Label>
              <Input type="datetime-local" value={form.deliverAt} onChange={(e) => setForm({ ...form, deliverAt: e.target.value })} />
              <p className="text-xs text-muted-foreground">Leave empty to send immediately</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="send-notification-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {form.deliverAt ? 'Schedule' : 'Send Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
